
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Getter, namespace } from "vuex-class";
import Multiselect from "vue-multiselect";
import { UserContactDetails, UserContactDetailsPagedResponse, UserContactDetailsRequest } from "@/store/tasks/types";
import { PaginatedSearchQueryParams } from "@/store/types";
import GenericTable, { TableHeader } from "../../table/generic-table.vue";

const Tasks = namespace("tasks");
const SiteMonitor = namespace("siteMonitor");


@Component({
	components: {
		multiselect: Multiselect,
		"generic-table": GenericTable
	}
})
export default class CallContactTask extends Vue {
	@Getter getUserName: string;
	@SiteMonitor.Action createAuditRecord: any;
	@SiteMonitor.Getter("getEventDetails") eventDetails: any;
	@Tasks.Action fetchCallContacts: (payload: UserContactDetailsRequest) => Promise<void>;
	@Tasks.Getter("getCallContacts") getCallContacts: UserContactDetailsPagedResponse;

	@Prop() public task!: any;
	@Prop(String) public result!: string;
	@Prop(String) public eventTaskID!: string;
	
	private renderType: any = { name: "", value: "" };

	private callName: string = "";
	private callList: UserContactDetails[] = [];
	private callListIndex: number = 0;
	private isContactsLoading: boolean = false;
	private totalRecords: number = 0;

	private defaultPageNumber: number = 1;
	private defaultPageSize: number = 20;

	private contactRoleRenderType: string = "Contact Role";

	private columns: TableHeader[] = [
		{
			title: "Full Name",
			key: "fullName",
			order: 1,
			sortOrder: 0,
			sortOrderReversed: false,
			description: "The contacts full name",
			searchable: true,
			visible: true,
			dataType: "string",
			isTermLabel: true,
			sortable: false,
			isSortedByDefault: false,
		},
		{
			title: "Telephone",
			key: "telephone",
			order: 2,
			sortOrder: 0,
			sortOrderReversed: false,
			description: "The contacts telephone number",
			searchable: false,
			visible: true,
			dataType: "string",
			isTermLabel: true,
			sortable: false,
			isSortedByDefault: false,
			useCustomCell: false
		},
		{
			title: "Email",
			key: "email",
			order: 3,
			sortOrder: 0,
			sortOrderReversed: false,
			description: "The contacts email",
			searchable: false,
			visible: true,
			dataType: "string",
			isTermLabel: true,
			sortable: false,
			isSortedByDefault: false,
		}];

	/** submit data to complete Task */
	private submitOutcome(outcome: string): void
	{
		this.$emit("result-changed", outcome);
		this.$emit("complete");
	}

	private get isContactRoleRenderType(): boolean
	{
		return this.renderType.name === this.contactRoleRenderType;
	}

	private get currentCaller(): UserContactDetails
	{
		return this.callList[this.callListIndex];
	}

	private callResultYes(): void
	{
		const mobile = this.callList[this.callListIndex]["mobile"];
		const telephone = this.callList[this.callListIndex]["telephone"];
		const bothNumbers = mobile && telephone ? true : false;

		if (this.renderType.value === "CallList") {
			// Set the currently selected users fullname to callName
			this.callName = this.callList[this.callListIndex]["fullname"];

			//Complete task with this user called.
			this.submitOutcome(
				`Call ${this.callName} (${mobile} ${
					bothNumbers ? "/" : ""
				} ${telephone}) :  Call Made`
			);
		}
		// If rendertype is call contact role
		else if (this.isContactRoleRenderType) {
			// Set the currently selected users fullname to callName
			this.callName = this.callList[this.callListIndex]["fullname"];

			//Complete task with this user called.
			this.submitOutcome(`Contacts for ${this.renderType.value.role} were displayed and have been closed.`);
		}
		// If rendertype is either FireFighters or Police
		else if (
			this.renderType.value === "Fire Dept" ||
			this.renderType.value === "Police"
		) {
			this.submitOutcome(
				` Call ${this.renderType.name} "${telephone}" ${this.getUserName} entry: "Call Made"`
			);
		}
	}

	private async callResultNo(): Promise<void>
	{
		const mobile = this.callList[this.callListIndex]["mobile"];
		const telephone = this.callList[this.callListIndex]["telephone"];
		const bothNumbers = mobile && telephone ? true : false;

		if (this.renderType.value == "CallList") {
			// Set the currently selected users fullname to callName
			this.callName = this.callList[this.callListIndex]["fullname"];

			//Check if we have ran out of users within the call list.
			if (this.callListIndex >= this.callList.length - 1) {
				//If no users are left, set complete task to be no users picked up.
				this.submitOutcome("No users picked up");

				// If it's not the last user and they didn't pick up the phone
			} else {
				// Increment to the next user
				this.callListIndex++;
			}

			// Create audit saying that the user didn't pick up the phone.
			await this.createAuditRecord({
				eventId: this.eventDetails.eventID,
				eventRecordTypeId: 111,
				details: `Call ${this.callName} (${mobile} ${
					bothNumbers ? "/" : ""
				} ${telephone}) : No Answer`
			});
		}
		// If rendertype is either FireFighters or Police
		else if (
			this.renderType.value == "Fire Dept" ||
			this.renderType.value == "Police"
		) {
			// Complete Task with msg below
			this.submitOutcome(
				` Call ${this.renderType.name} "${telephone}" ${this.getUserName} entry: "No Answer"`
			);
		}
	}

	private callResultExit(): void
	{
		const outcome = `The ${this.renderType.name} has not been setup or configured correctly.`;
		this.submitOutcome(outcome);
	}

	private async mounted(): Promise<void>
	{
		await this.configureCallContacts(null);
	}

	private async updateTableData(request: PaginatedSearchQueryParams) : Promise<void>
	{
		await this.configureCallContacts(request);
	}

	@Watch("eventDetails.groupID")
	private async configureCallContacts(request: PaginatedSearchQueryParams): Promise<void>
	{
		this.isContactsLoading = true;

		// Get taskText object that's created in setup.
		const settings = this.task.taskData;

		// Check if settings is not undefined and contains the desired key.
		if (settings !== null && settings !== undefined) {
			// Insert settings Name value in this.renderType
			this.renderType = settings;

			const payload: UserContactDetailsRequest = {
				groupId: this.eventDetails.groupID,
				pageNumber: request?.page ? request.page : this.defaultPageNumber,
				pageSize: request?.pageSize ? request.pageSize: this.defaultPageSize,
				contactRoleId: this.renderType.value.contactRoleId
			};

			try {
				await this.fetchCallContacts(payload);
			}
			catch {
				this.callList = [];
				this.isContactsLoading = false;
				return;
			}

			this.totalRecords = this.getCallContacts.totalRecords;
			await this.populateTableData();
		}
	}

	private async populateTableData(): Promise<void> {
		let newList: UserContactDetails[] = []

		if (this.getCallContacts.data) {
			this.getCallContacts.data.forEach(element => {
				if (this.isContactRoleRenderType && !newList.find(contact => contact.userId == element.userId)) {
					newList.push(element);
				}
			});
		}

		this.callList = newList;
		this.isContactsLoading = false;
	}
}
